/**
 * Validates the result from the Google Maps Geocoder based on specific criteria.
 *
 * - The result contains non-empty address components.
 * - The result's types array contains a valid type as the first item.
 *
 * @param results - An array of `google.maps.GeocoderResult` objects to validate.
 * @returns `true` if the result meets the criteria, otherwise `false`.
 */
export const validateGeocoderResult = (results: Array<google.maps.GeocoderResult>) => {
  if (!results) {
    return false;
  }

  const result = results[0];

  // Ensure address_components is non-empty.
  if (!result?.address_components || result?.address_components.length === 0) {
    return false;
  }

  // We don't want "locality", "country", "political" etc.
  if (
    !result.types?.[0] ||
    !["street_address", "premise", "subpremise", "establishment"].includes(result.types[0])
  ) {
    return false;
  }

  return true;
};

/**
 * Guesses an address using the Google Maps Geocoder service.
 *
 * @param address - The address to geocode. Defaults to an empty string.
 * @returns A promise that resolves to a `google.maps.GeocoderResult` if the geocoding is successful and valid, or `undefined` if the geocoding fails or times out.
 *
 * The function will return `undefined` immediately if the address is empty or the Google Maps Geocoder service is unavailable.
 * The geocoding request will timeout after 1000 milliseconds if no response is received.
 *
 * The geocoding is restricted to addresses within the United States.
 */
export const guessAddressUsingGeocoder = async (
  address = ""
): Promise<google.maps.GeocoderResult | undefined> | undefined => {
  const timeoutDuration = 1000;

  // Return undefined if the geocoder is unavailable or the address is empty.
  if (address === "" || !window?.google?.maps?.Geocoder) {
    return undefined;
  }

  // Return a promise that resolves based on geocode result or timeout.
  return new Promise((resolve) => {
    const geocoder: google.maps.Geocoder = new window.google.maps.Geocoder();

    // Resolve as undefined if geocode takes too long.
    const timeoutId = setTimeout(() => {
      resolve(undefined);
    }, timeoutDuration);

    geocoder.geocode({ address }, (results, status: string) => {
      clearTimeout(timeoutId);
      if (status === "OK" && validateGeocoderResult(results)) {
        resolve(results[0]);
      } else {
        resolve(undefined);
      }
    });
  });
};

/**
 * Triggers a "keydown" event with the "ArrowDown" key on the specified input field.
 *
 * @param inputField - The HTMLInputElement on which to trigger the event.
 */
export const triggerArrowDownKey = (inputField: HTMLInputElement) => {
  inputField.dispatchEvent(
    new KeyboardEvent("keydown", {
      key: "ArrowDown",
      keyCode: 40,
      code: "ArrowDown",
      which: 40,
      bubbles: false
    })
  );
};

/**
 * Simulates the pressing of the Enter key on a given input field.
 *
 * @param inputField - The HTML input element on which to trigger the Enter key event.
 */
export const triggerEnterKey = (inputField: HTMLInputElement) => {
  inputField.dispatchEvent(
    new KeyboardEvent("keydown", {
      key: "Enter",
      keyCode: 13,
      code: "Enter",
      which: 13,
      bubbles: false
    })
  );
};

/**
 * A key down event handler that intercepts the "Enter" key and translates it into "ArrowDown" and
 * "Enter" key events that simulate selecting an address.
 *
 * @param event - The keyboard event triggered by the user.
 */
export const interceptEnterKeySelection: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
  if (event.key === "Enter") {
    event.preventDefault();
    triggerArrowDownKey(event.target as HTMLInputElement);
    triggerEnterKey(event.target as HTMLInputElement);
  }
};
